<template>
  <div class="px-4 px-lg-12 py-12 d-flex flex-column align-center" style="height: 100%; width: 100%">
    <div class="mb-10 text-center">
      <div class="text-h5 font-weight-bold">Verify Email And Phone Number</div>
      <div class="text-subtitle-1 mt-3">
        Please enter the 6-digit verification codes that were sent to
        {{ newRegistration.email }} and {{ newRegistration.phone }}. Codes are valid for 30
        minutes.
      </div>
    </div>

    <v-form
      :style="{width: $vuetify.breakpoint.mdAndDown ? '100%': '75%'}"
      @submit.prevent="submit"
      lazy-validation
      ref="form"
    >
      <v-text-field
        :rules="[rules.required]"
        name="emailVerificationCode"
        outlined
        prepend-inner-icon="mdi-form-textbox-password"
        type="number"
        v-model="emailVerificationCode"
      >
        <template #label>Email Verification Code *</template>
        <template #append>
          <v-tooltip :disabled="resendEmailSubmitted" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                :disabled="disableEmailResendBtn"
                @click="resendVerificationCodes('EMAIL')"
                v-bind="attrs"
                v-on="on"
              >mdi-email-sync</v-icon>
            </template>
            <span>Resend Code</span>
          </v-tooltip>
        </template>
      </v-text-field>

      <v-text-field
        :rules="[rules.required]"
        name="phoneVerificationCode"
        outlined
        prepend-inner-icon="mdi-form-textbox-password"
        type="number"
        v-model="phoneVerificationCode"
      >
        <template #label>Phone Number Verification Code *</template>
        <template #append>
          <v-tooltip :disabled="resendSMSSubmitted" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                :disabled="disableSMSResendBtn"
                @click="resendVerificationCodes('SMS')"
                v-bind="attrs"
                v-on="on"
              >mdi-phone-sync</v-icon>
            </template>
            <span>Resend Code</span>
          </v-tooltip>
        </template>
      </v-text-field>

      <div class="d-flex justify-center">
        <div
          class="text-caption text--secondary text-center"
          v-if="disableEmailResendBtn && (60 - resendEmailTimeout) > 0"
        >Resend email in {{ 60 - resendEmailTimeout }}s</div>
        <v-divider
          class="mx-4"
          v-if="disableEmailResendBtn && (60 - resendEmailTimeout) > 0 && disableSMSResendBtn && (60 - resendSMSTimeout) > 0"
          vertical
        />
        <div
          class="text-caption text--secondary text-center"
          v-if="disableSMSResendBtn && (60 - resendSMSTimeout) > 0"
        >Resend SMS in {{ 60 - resendSMSTimeout }}s</div>
      </div>

      <v-btn :loading="isSubmitting" block class="mt-3" color="primary" large type="submit">Verify</v-btn>
      <v-btn :disabled="isSubmitting" block class="mt-2" text color="error" large type="button" @click="onCancel">Cancel</v-btn>
    </v-form>
  </div>
</template>

<script>
import moment from "moment";
import { rules } from "@/utils/validatorUtil";
import { instituteService } from "@/services";

export default {
  name: "Verification",

  data: () => ({
    newRegistration: JSON.parse(sessionStorage.getItem("newRegistration") || "{}"),
    emailVerificationCode: "",
    phoneVerificationCode: "",
    isSubmitting: false,
    resendEmailSubmitted: false,
    resendEmailTimeout: 0,
    resendSMSSubmitted: false,
    resendSMSTimeout: 0,
  }),

  computed: {
    rules() {
      return rules;
    },

    disableEmailResendBtn() {
      return this.resendEmailSubmitted || this.resendEmailTimeout < 60;
    },

    disableSMSResendBtn() {
      return this.resendSMSSubmitted || this.resendSMSTimeout < 60;
    },
  },

  created() {
    this.resendEmailTimeout = moment().diff(moment(this.newRegistration.emailRequestedAt), "seconds");
    this.resendSMSTimeout = moment().diff(moment(this.newRegistration.smsRequestedAt), "seconds");

    if (this.resendEmailTimeout < 60) {
      this.startTimer("EMAIL");
    }

    if (this.resendSMSTimeout < 60) {
      this.startTimer("SMS");
    }
  },

  methods: {
    startTimer(method) {
      const timer = setInterval(() => {
        if (method === "EMAIL") {
          this.resendEmailTimeout = moment().diff(moment(this.newRegistration.emailRequestedAt), "seconds");
          if (this.resendEmailTimeout > 60) {
            this.resendEmailSubmitted = false;
            clearInterval(timer);
          }
        } else {
          this.resendSMSTimeout = moment().diff(moment(this.newRegistration.smsRequestedAt), "seconds");
          if (this.resendSMSTimeout > 60) {
            this.resendSMSSubmitted = false;
            clearInterval(timer);
          }
        }
      }, 1000);
    },

    async resendVerificationCodes(method) {
      try {
        if (method === "EMAIL") {
          await instituteService.resendEmailVerificationCode(this.newRegistration.registrationCode);
          this.resendEmailSubmitted = true;

          sessionStorage.setItem(
            "newRegistration",
            JSON.stringify({ ...this.newRegistration, emailRequestedAt: new Date() })
          );
          this.newRegistration = JSON.parse(sessionStorage.getItem("newRegistration") || "{}");
          this.startTimer("EMAIL");
        } else {
          await instituteService.resendPhoneVerificationCode(this.newRegistration.registrationCode);
          this.resendSMSSubmitted = true;

          sessionStorage.setItem(
            "newRegistration",
            JSON.stringify({ ...this.newRegistration, smsRequestedAt: new Date() })
          );
          this.newRegistration = JSON.parse(sessionStorage.getItem("newRegistration") || "{}");
          this.startTimer("SMS");
        }
      } catch (error) {
        this.$snackbar.show({
          message: error?.response?.data?.message,
          color: this.$colors.error,
          horizontal: "right",
        });
      }
    },

    async submit() {
      if (this.$refs.form.validate()) {
        try {
          this.isSubmitting = true;

          const requestData = {
            registrationCode: this.newRegistration.registrationCode,
            mobileCode: this.phoneVerificationCode,
            emailCode: this.emailVerificationCode,
          };
          await instituteService.submitVerificationCodes(requestData);

          // update newRegistration object
          const newRegistration = JSON.stringify({
            ...this.newRegistration,
            verified: 1,
          });
          sessionStorage.setItem("newRegistration", newRegistration);

          this.$router.push({ name: "InstituteDetails" });
        } catch (error) {
          this.$snackbar.show({
            message: error?.response?.data?.message,
            color: this.$colors.error,
            horizontal: "right",
          });
        } finally {
          this.isSubmitting = false;
        }
      }
    },
    onCancel() {
      sessionStorage.removeItem("newRegistration");
      location.reload();
    }
  },
};
</script>
